import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { config } from "../environment";
import { gatedMutate } from "../hooks/serverGate";
import { useFollow3b } from "../hooks/useFollow3b";
import { useGetPresence } from "../hooks/useGetPresence";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsGitSynced } from "../hooks/useIsGitSynced";
import { useInspectShortcut } from "../hooks/useInspectShortcut";
import { useIsMobile } from "../hooks/useIsMobile";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { useQuery } from "../hooks/useQuery";
import { useSelectedExecution } from "../hooks/useSelectedExecution";
import { useRunExecutions } from "../hooks/useStepExecutions";
import { useSetPresence } from "../hooks/useSetPresence";
import type { StepTestFixtureSetupRequest } from "../hooks/useStepTestPin";
import { markBranchAwaitingAiName } from "../lib/aiBranchNames";
import { computeChangedStepDirs } from "../lib/changedStepDirs";
import type { StatusState } from "../lib/chatStatus";
import type { PushLiveBranch } from "../lib/executePushLive";
import { resolveFollowTarget } from "../lib/followTarget";
import { hasPushableChanges } from "../lib/hasPushableChanges";
import { clearStepFiles } from "../lib/lastOpenStepFile";
import { layoutWorkflowSteps } from "../lib/layoutWorkflowSteps";
import { type ParsedStep, parseCommitData } from "../lib/parseCommitData";
import { rankIsolatedStep } from "../lib/rankIsolatedStep";
import { resolveWorkflowFileHref } from "../lib/resolveWorkflowFileHref";
import {
  type BashFileTarget,
  parseBashFileTarget,
  type StreamingBashWrite,
  type StreamingFileEdit,
} from "../lib/parseStreamingBashWrite";
import { playPop } from "../lib/sounds";
import { makeUrl, navigate, routes, useCurrentUrl } from "../lib/urls";
import { type DriveCommand, driveLiveStep } from "../lib/webpageDebugBridge";
import {
  approvePlan,
  askQuestion,
  bash,
  type ChatTool,
  connectToApp,
  copyUploadToStep,
  editFile,
  execute,
  getPageConsole,
  getRunExecutions,
  getStepLastExecution,
  inspectRenderedPage,
  interactWithPage,
  listVersions,
  pushLive,
  readFile,
  readVersionChat,
  runStep,
  screenshotPage,
  useSkill,
  webFetch,
  webSearch,
  writeFile,
} from "../tools";
import { Backdrop } from "./Backdrop";
import { BranchSelector } from "./BranchSelector";
import { Button } from "./Button";
import { Chat, type ChatHandle, type ChatProps } from "./Chat";
import { useRecapTrigger } from "../hooks/useRecapTrigger";
import {
  gettingStartedSuggestions,
  workflowSuggestions,
} from "./Chat/workflowSuggestions";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import type { CodeSelection } from "./CodeEditor";
import { ConnectClaimPrompt } from "./ConnectClaimPrompt";
import { EditableWorkflowName } from "./EditableWorkflowName";
import { GhostedStepGrid, ghostStepMapHeight } from "./GhostedWorkflowEmpty";
import { DeletedOnBranchBanner } from "./DeletedOnBranchBanner";
import { HistoryBanner } from "./HistoryBanner";
import { ImportWorkflowModal } from "./ImportWorkflowModal";
import { MainScrollContainer } from "./MainScrollContainer";
import { forceMaximizedLiveBrowserView, MaximizedStep } from "./MaximizedStep";
import { Resizable } from "./Resizable";
import { RunWaterfall } from "./RunWaterfall";
import { SpaceBadge } from "./SpaceBadge";
import { StatusIndicator } from "./StatusIndicator";
import {
  getStepMapPadding,
  gridPadding,
  gridStepHeight,
  rowGap,
  StepMap,
} from "./StepMap";
import { selectStepFiles } from "./stepFilesDiff";
import { Text } from "./Text";
import { useToast } from "./Toast";
import { WorkflowActionsMenu } from "./WorkflowActionsMenu";
import { WorkflowConnectorButton } from "./WorkflowConnectorButton";
import { WorkflowOverview } from "./WorkflowOverview";

export type WorkflowData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadWorkflow>>
>;
export type BranchData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadBranch>>
>;
export type BranchSummary = WorkflowData["branches"][number];
export type CommitData = BranchData["commits"][number];

export type StepData = ParsedStep;
export type ChatEntryData = QueryRowType<
  ReturnType<typeof queries.loadCommitChat>
>["entries"][number];

export type CodeSnippet = {
  id: string;
  filePath: string;
  from: number;
  to: number;
  fromLine: number;
  toLine: number;
  fromChar: number;
  toChar: number;
  text: string;
  color: string;
  revision?: "current" | "previous";
};

export type ElementSnippet = {
  id: string;
  stepName: string;
  tagName: string;
  descriptor: string;
  label?: string;
  cssPath?: string;
  outerHtml: string;
  textContent: string;
  color: string;
  index: number;
};

type Props = {
  spaceId: string;
  workflowId: string;
  step?: string;
};

const Layout = styled("div")`
  display: flex;
  flex-direction: row;
  height: 100%;
  gap: 12px;
  padding: 12px;
  padding-top: 1px;
  overflow: hidden;
`;

const Vertical = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 0;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--elevation-low);
`;

const ChatContainer = styled("div")`
  display: flex;
  flex-direction: column;
  width: 448px;
`;

const DragHandle = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 12px;
  cursor: ns-resize;
  user-select: none;

  & [data-drag-indicator] {
    opacity: 0;
    transition: opacity 0ms;
  }

  &:hover [data-drag-indicator] {
    opacity: 1;
  }
`;

const ChatResizeHandle = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 20px;
  cursor: ew-resize;
  user-select: none;
  z-index: 1;

  & [data-drag-indicator] {
    opacity: 0;
    transition: opacity 0ms;
  }

  &:hover [data-drag-indicator] {
    opacity: 1;
  }
`;

const FOLLOW_CODE_TOOLS = new Set(["editFile", "writeFile", "readFile"]);
const FOLLOW_OUTPUT_TOOLS = new Set([
  "runStep",
  "execute",
  "inspectRenderedPage",
  "getPageConsole",
  "interactWithPage",
  "screenshotPage",
]);

const MIN_CHAT_WIDTH = 340;
const MAX_CHAT_WIDTH = 780;
const CHAT_WIDTH_KEY = "chatPanelWidth";

function loadChatWidth(): number | undefined {
  const stored = localStorage.getItem(CHAT_WIDTH_KEY);
  if (stored == null) return undefined;
  const n = Number(stored);
  return Number.isFinite(n) ? n : undefined;
}

function saveChatWidth(width: number | undefined) {
  if (width == null) {
    localStorage.removeItem(CHAT_WIDTH_KEY);
  } else {
    localStorage.setItem(CHAT_WIDTH_KEY, String(width));
  }
}

export function Workflow({ spaceId, workflowId, step }: Props) {
  const isMobile = useIsMobile();
  const z = useZero();
  const {
    connectingClaimId,
    branchId: urlBranchId,
    commitId: urlCommitId,
    showRuns,
    tab,
  } = useCurrentUrl(routes.space.workflow);
  const [workflow, workflowQuery] = useQuery(
    queries.loadWorkflow({ workflowId })
  );
  const [userFavorites] = useQuery(queries.userFavoriteWorkflowIds());
  const [allConnectorsRaw] = useQuery(queries.connectedAppsIndex());
  const [spaceGrants] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const rbacCanWrite = useHasAccess("space:write", spaceId);
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const isGitSynced = useIsGitSynced(spaceId);
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();
  const allConnectors = useMemo(
    () =>
      allConnectorsRaw.filter((app) =>
        isSupplierAvailableFromGrants(
          "space_uses_connected_apps",
          spaceGrants,
          app.roleGrantsAsScope,
          userGroupMemberships
        )
      ),
    [allConnectorsRaw, spaceGrants, userGroupMemberships]
  );
  const [branch, branchQuery] = useQuery(
    queries.loadBranch({
      workflowId,
      branchId: urlBranchId || undefined,
    })
  );
  const branchId = branch?.id;
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId ?? "");

  const isOnMainBranch = branch?.name === "main";
  const branchHeadCommitId = branch?.headCommitId ?? null;

  // Git sync tombstoned this workflow on this branch: it's read-only until
  // restored, since edits here would never sync back to the remote.
  const isDeletedOnBranch = (workflow?.branches ?? []).some(
    (b) => b.id === branch?.id && b.spaceBranchMembers.length > 0
  );
  const canWrite = rbacCanWrite && !isDeletedOnBranch;

  const isViewingHistory = !!urlCommitId && urlCommitId !== branchHeadCommitId;
  const commit = isViewingHistory
    ? branch?.commits.find((c) => c.id === urlCommitId)
    : branch?.headCommit;

  const [headCommitChats] = useQuery(
    queries.loadCommitChat({
      commitId: isOnMainBranch ? (branchHeadCommitId ?? "") : "",
    })
  );
  const [viewedCommitChats] = useQuery(
    queries.loadCommitChat({
      commitId: isOnMainBranch && isViewingHistory ? (commit?.id ?? "") : "",
    })
  );
  const [branchCommits] = useQuery(
    queries.loadBranchChats({
      branchId: !isOnMainBranch ? (branch?.id ?? "") : "",
    })
  );

  const [isDeploying, setIsDeploying] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [pendingZip, setPendingZip] = useState<File | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const topPaneRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<ChatHandle>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [hoveredStepCanonicalId, setHoveredStepCanonicalId] = useState<
    string | null
  >(null);
  const [monitorHoveredStepIds, setMonitorHoveredStepIds] =
    useState<Set<string> | null>(null);
  const [runsTabStepIds, setRunsTabStepIds] = useState<Set<string> | null>(
    null
  );
  const handleMonitorStepsHover = useCallback(
    (canonicalIds: ReadonlyArray<string> | null) => {
      setMonitorHoveredStepIds(canonicalIds ? new Set(canonicalIds) : null);
    },
    []
  );
  const handleRunsTabStepsChange = useCallback(
    (canonicalIds: ReadonlyArray<string> | null) => {
      setRunsTabStepIds(canonicalIds ? new Set(canonicalIds) : null);
    },
    []
  );

  // Waterfall pane resize (bottom pane height in px, null = ideal or 50%).
  const [waterfallHeight, setWaterfallHeight] = useState<number | null>(null);
  const [waterfallIdealHeight, setWaterfallIdealHeight] = useState<
    number | null
  >(null);

  const MIN_PANE_HEIGHT = 240;

  const onWaterfallHandleDown = (e: React.MouseEvent) => {
    e.preventDefault();
    let dragging = true;
    const onMove = (ev: MouseEvent) => {
      if (!dragging) return;
      const container = topPaneRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const handleGap = 12;
      const newHeight = Math.max(
        MIN_PANE_HEIGHT,
        rect.bottom - ev.clientY - handleGap
      );
      let maxHeight = rect.height - MIN_PANE_HEIGHT - handleGap;
      if (waterfallIdealHeight != null) {
        maxHeight = Math.min(maxHeight, waterfallIdealHeight);
      }
      setWaterfallHeight(Math.min(newHeight, maxHeight));
    };
    const onUp = () => {
      dragging = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Chat panel horizontal resize.
  const layoutRef = useRef<HTMLDivElement>(null);
  const [chatWidth, setChatWidth] = useState<number | undefined>(loadChatWidth);
  const chatDraggingRef = useRef(false);

  const onChatResizeHandleDown = (e: React.MouseEvent) => {
    e.preventDefault();
    let dragging = true;
    chatDraggingRef.current = true;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev: MouseEvent) => {
      if (!dragging) return;
      const layout = layoutRef.current;
      if (!layout) return;
      const rect = layout.getBoundingClientRect();
      const style = getComputedStyle(layout);
      const paddingLeft = parseFloat(style.paddingLeft);
      const newWidth = ev.clientX - rect.left - paddingLeft;
      const clamped = Math.max(
        MIN_CHAT_WIDTH,
        Math.min(newWidth, MAX_CHAT_WIDTH)
      );
      setChatWidth(clamped);
      saveChatWidth(clamped);
    };
    const onUp = () => {
      dragging = false;
      chatDraggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleChatResizeDoubleClick = () => {
    setChatWidth(undefined);
    saveChatWidth(undefined);
  };

  const isFavorite = userFavorites.some((f) => f.workflowId === workflowId);

  const [historyCommitFiles, historyFilesQuery] = useQuery(
    queries.loadAllCommitFiles({
      commitId: urlCommitId || "",
    })
  );
  const activeCommitFiles = useMemo(
    () =>
      isViewingHistory ? historyCommitFiles : (branch?.headCommit?.files ?? []),
    [isViewingHistory, historyCommitFiles, branch?.headCommit?.files]
  );
  const commitFilesLoading = isViewingHistory
    ? historyFilesQuery.type !== "complete"
    : branchQuery.type !== "complete";

  const connectorsById = useMemo(
    () => new Map(allConnectors.map((ca) => [ca.id, ca])),
    [allConnectors]
  );

  const [appSlugRows] = useQuery(queries.workflowAppSlugs({ workflowId }));
  const slugToAppId = useMemo(
    () => new Map(appSlugRows.map((r) => [r.subKey, r.connectedAppId])),
    [appSlugRows]
  );

  const [stepActivity, stepActivityQuery] = useQuery(
    branch
      ? queries.loadStepActivity({ workflowId, branchId: branch.id })
      : undefined
  );
  const stepCounterMap = useMemo(
    () =>
      new Map(
        (stepActivity ?? []).map((row) => [
          row.stepDir,
          {
            executionCount: row.executionCount ?? 0,
            lastStatus: row.latestStatus ?? null,
            lastKind: row.latestKind ?? null,
            lastExecutionId: row.latestExecutionId ?? null,
            lastCompletedAt: row.latestCompletedAt ?? null,
          },
        ])
      ),
    [stepActivity]
  );

  // Load fork and head commit files separately for change detection on
  // non-main branches. These no-op on main (empty commitId).
  const forkCommitId =
    !isOnMainBranch && branch?.forkCommitId ? branch.forkCommitId : "";
  const headDiffCommitId =
    !isOnMainBranch &&
    branch?.headCommitId &&
    branch?.forkCommitId !== branch?.headCommitId
      ? branch.headCommitId
      : "";
  const [forkCommitFiles, forkFilesQuery] = useQuery(
    queries.loadAllCommitFiles({
      commitId: forkCommitId,
    })
  );
  const [headDiffCommitFiles, headDiffFilesQuery] = useQuery(
    queries.loadAllCommitFiles({
      commitId: headDiffCommitId,
    })
  );

  // Detect which steps have changed on this branch compared to main.
  // Only applies to branches forked from main (have a forkCommitId).
  // Only computed once the branch query is complete to avoid a flash where
  // all steps appear changed while data is still streaming in.
  const changedCanonicalIds = useMemo(() => {
    if (isOnMainBranch || !branch || branchQuery.type !== "complete")
      return new Set<string>();
    if (!branch.forkCommitId) return new Set<string>();
    if (branch.forkCommitId === branch.headCommitId) return new Set<string>();
    return computeChangedStepDirs(forkCommitFiles, headDiffCommitFiles);
  }, [
    isOnMainBranch,
    branch,
    branchQuery.type,
    forkCommitFiles,
    headDiffCommitFiles,
  ]);

  // Wait for both file queries to complete before deciding. headCommitId
  // updates instantly when a new commit lands, but its commit_files take
  // seconds to sync — comparing against an empty set during that gap shows
  // false-positive changes. forkCommitId may be null on a brand-new
  // workflow (main is created on first push), in which case we compare
  // against an empty fork side.
  const hasChangesToPush = useMemo(() => {
    if (isOnMainBranch || !branch || branchQuery.type !== "complete")
      return false;
    if (!branch.headCommitId) return false;
    if (branch.forkCommitId === branch.headCommitId) return false;
    if (branch.respondingAt != null) return false;
    if (
      forkFilesQuery.type !== "complete" ||
      headDiffFilesQuery.type !== "complete"
    )
      return false;
    return hasPushableChanges(forkCommitFiles, headDiffCommitFiles);
  }, [
    isOnMainBranch,
    branch,
    branchQuery.type,
    forkFilesQuery.type,
    headDiffFilesQuery.type,
    forkCommitFiles,
    headDiffCommitFiles,
  ]);

  const { steps, links, workflowReadme } = useMemo(
    () =>
      parseCommitData(
        activeCommitFiles,
        connectorsById,
        stepCounterMap,
        slugToAppId
      ),
    [activeCommitFiles, connectorsById, stepCounterMap, slugToAppId]
  );

  const selectedStep = steps.find((s) => s.name === step);

  const [codeSnippets, setCodeSnippets] = useState<CodeSnippet[]>([]);
  const stepRef = useRef(selectedStep);
  stepRef.current = selectedStep;
  const handleCodeSelectionChange = useCallback(
    (selections: CodeSelection[]) => {
      if (selections.length === 0) return;
      const stepName = stepRef.current?.name ?? "";
      const color = stepRef.current?.color ?? "sky";
      const enriched = selections.map((s) => ({
        ...s,
        filePath: `${stepName}/${s.filePath}`,
        id: `${stepName}/${s.id}`,
        color,
      }));
      setCodeSnippets((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        const added = enriched.filter((s) => !seen.has(s.id));
        return added.length ? [...prev, ...added] : prev;
      });
    },
    []
  );
  const removeCodeSnippet = useCallback((id: string) => {
    setCodeSnippets((prev) => prev.filter((s) => s.id !== id));
  }, []);
  const clearCodeSnippets = useCallback(() => setCodeSnippets([]), []);

  const [elementSnippets, setElementSnippets] = useState<ElementSnippet[]>([]);
  const [htmlInspectMode, setHtmlInspectMode] = useState(false);
  useInspectShortcut(() => setHtmlInspectMode((v) => !v));
  const selectedStepColor = selectedStep?.color ?? "sky";
  const selectedStepName = selectedStep?.name;
  useEffect(() => {
    setElementSnippets((prev) =>
      prev.map((s) =>
        s.stepName === selectedStepName && s.color !== selectedStepColor
          ? { ...s, color: selectedStepColor }
          : s
      )
    );
  }, [selectedStepColor, selectedStepName]);
  const handleElementSelection = useCallback(
    (element: Omit<ElementSnippet, "stepName" | "color">) => {
      const stepName = stepRef.current?.name ?? "";
      const color = stepRef.current?.color ?? "sky";
      const enriched = { ...element, stepName, color };
      setElementSnippets((prev) => {
        if (
          element.cssPath &&
          prev.some(
            (s) => s.stepName === stepName && s.cssPath === element.cssPath
          )
        )
          return prev;
        return [...prev, enriched];
      });
    },
    []
  );
  const removeElementSnippet = useCallback((id: string) => {
    setElementSnippets((prev) => {
      const snippet = prev.find((s) => s.id === id);
      if (snippet) {
        window.dispatchEvent(
          new CustomEvent("3b-deselect-element", {
            detail: { index: snippet.index },
          })
        );
      }
      return prev.filter((s) => s.id !== id);
    });
  }, []);
  const deselectElementByIndex = useCallback((index: number) => {
    setElementSnippets((prev) => prev.filter((s) => s.index !== index));
  }, []);
  const clearElementSnippets = useCallback(() => setElementSnippets([]), []);

  const { selected: selectedExecution, latestId: latestExecutionId } =
    useSelectedExecution(selectedStep?.id ?? "", workflowId, branch?.id ?? "");
  const selectedRunId = selectedStep
    ? (selectedExecution?.runId ?? null)
    : null;

  const { executions: selectedRunExecutions } = useRunExecutions(selectedRunId);

  const activeRunCanonicalIds = useMemo(() => {
    if (!selectedRunId || selectedRunExecutions.length === 0) return null;
    return new Set(selectedRunExecutions.map((e) => e.stepDir!));
  }, [selectedRunId, selectedRunExecutions]);

  const soundsEnabled =
    !isViewingHistory &&
    Boolean(workflow) &&
    stepActivityQuery.type === "complete";

  useSetPresence(workflowId, branch?.id, selectedStep?.id);

  useEffect(() => {
    const mark = () => {
      z.mutate(sharedMutators.markWorkflowViewed({ workflowId }));
    };
    mark();
    return mark;
  }, [z, workflowId]);

  useEffect(() => () => clearStepFiles(), [workflowId]);

  const { presentByStep, presentBranchesByUser } = useGetPresence();

  const branchStart = isOnMainBranch ? commit : branch?.commits[0];

  const headCommitChat = isOnMainBranch
    ? headCommitChats[0]
    : branchCommits.find((c) => c.id === branchHeadCommitId)?.chats;
  const headCommitChatId = headCommitChat?.id ?? null;
  const entries = useMemo(
    () =>
      isOnMainBranch
        ? isViewingHistory
          ? (viewedCommitChats[0]?.entries ?? [])
          : (headCommitChats[0]?.entries ?? [])
        : branchCommits.flatMap((c) => c.chats?.entries ?? []),
    [
      isOnMainBranch,
      isViewingHistory,
      viewedCommitChats,
      headCommitChats,
      branchCommits,
    ]
  );

  useRecapTrigger({
    chatId: isViewingHistory ? null : headCommitChatId,
    entries,
    responding: branch?.respondingAt != null,
    active: canWrite,
  });

  // Download always uses the head commit's chat on main (not the viewed
  // historical one). On non-main branches this is the same as `entries`.
  const downloadChatEntries = isOnMainBranch
    ? (headCommitChats[0]?.entries ?? [])
    : entries;

  const respondingToolInput = branch?.respondingAt
    ? (entries.findLast((e) => e.entryType === "AssistantToolUse")?.toolCall
        ?.input as Record<string, string>)
    : undefined;
  const aiPresentOnStepName =
    respondingToolInput?.step ??
    respondingToolInput?.to ??
    respondingToolInput?.from;

  const [streamingAgentStep, setStreamingAgentStep] = useState<
    string | undefined
  >();
  const [streamingFileWrite, setStreamingFileWrite] =
    useState<StreamingBashWrite | null>(null);
  const [streamingFileEdit, setStreamingFileEdit] =
    useState<StreamingFileEdit | null>(null);
  const [streamingFileTarget, setStreamingFileTarget] =
    useState<BashFileTarget | null>(null);

  // Walk back through the current turn's tool calls and bail on the first
  // non-bash tool so a newer runStep doesn't get shadowed by the previous
  // bash's file target.
  const finalizedFileTarget = useMemo<BashFileTarget | null>(() => {
    if (!branch?.respondingAt) return null;
    const turnStart =
      entries.findLastIndex((e) => e.entryType === "UserMessage") + 1;
    for (let i = entries.length - 1; i >= turnStart; i--) {
      const entry = entries[i]!;
      if (entry.entryType !== "AssistantToolUse") continue;
      const command = (entry.toolCall?.input as { command?: string } | null)
        ?.command;
      if (!command) return null;
      const target = parseBashFileTarget(command);
      if (target) return target;
    }
    return null;
  }, [branch?.respondingAt, entries]);

  const stepNames = useMemo(() => new Set(steps.map((s) => s.name)), [steps]);
  const stepNameList = useMemo(() => steps.map((s) => s.name), [steps]);
  const resolveChatFileHref = useCallback(
    (href: string) => resolveWorkflowFileHref(href, stepNameList),
    [stepNameList]
  );
  const followTarget = resolveFollowTarget({
    streamingFileWrite,
    streamingFileTarget,
    finalizedFileTarget,
    streamingAgentStep,
    aiPresentOnStepName,
    stepNames,
  });

  const [preferences] = useQuery(queries.userPreferences());
  const follow3bByDefault = preferences?.follow3bByDefault !== "off";
  const suggestFollow3b = preferences?.suggestFollow3b !== "off";

  const { isFollowing: isFollowing3b, setIsFollowing: setIsFollowing3b } =
    useFollow3b({
      agentStepName: followTarget.step,
      agentFilePath: followTarget.filePath,
      latestExecutionId,
      isResponding: branch?.respondingAt != null,
      autoFollow: follow3bByDefault,
      branchId,
    });

  const expandOutput = useMemo<boolean>(() => {
    if (!branch?.respondingAt) return false;
    if (followTarget.filePath) return false;
    const turnStart =
      entries.findLastIndex((e) => e.entryType === "UserMessage") + 1;
    for (let i = entries.length - 1; i >= turnStart; i--) {
      const name = entries[i]!.toolCall?.toolName;
      if (!name) continue;
      if (FOLLOW_OUTPUT_TOOLS.has(name)) return true;
      if (FOLLOW_CODE_TOOLS.has(name)) return false;
      if (name === "bash") {
        const command = (entries[i]!.toolCall?.input as { command?: string })
          ?.command;
        if (command && parseBashFileTarget(command)) return false;
      }
    }
    return false;
  }, [branch?.respondingAt, followTarget.filePath, entries]);

  const [followStatus, setFollowStatus] = useState<StatusState>("resting");
  const { showToast, hideToast } = useToast();
  const followToastShownRef = useRef(false);
  useEffect(() => {
    const showFollowToast = isFollowing3b && followStatus !== "resting";
    if (showFollowToast) {
      showToast({
        icon: <StatusIndicator state={followStatus} focused />,
        message: "Following 3B",
        buttonLabel: "Stop",
        onButtonClick: () => setIsFollowing3b(false),
        persist: true,
      });
      followToastShownRef.current = true;
    } else if (followToastShownRef.current) {
      hideToast();
      followToastShownRef.current = false;
    }
  }, [isFollowing3b, setIsFollowing3b, followStatus, showToast, hideToast]);
  useEffect(
    () => () => {
      if (followToastShownRef.current) {
        hideToast();
        followToastShownRef.current = false;
      }
    },
    [hideToast]
  );

  const [showFollowNudge, setShowFollowNudge] = useState(false);
  const followNudgeFiredForBranchRef = useRef<string | null>(null);
  const respondingAt = branch?.respondingAt;
  const hasPriorAssistantEntry = entries.some(
    (e) =>
      e.entryType === "AssistantMessage" || e.entryType === "AssistantToolUse"
  );
  useEffect(() => {
    if (respondingAt == null) return;
    if (isFollowing3b) return;
    if (hasPriorAssistantEntry) return;
    if (follow3bByDefault) return;
    if (!suggestFollow3b) return;
    const branchKey = branchId ?? "main";
    if (followNudgeFiredForBranchRef.current === branchKey) return;
    const show = setTimeout(() => {
      followNudgeFiredForBranchRef.current = branchKey;
      setShowFollowNudge(true);
      playPop();
    }, 1000);
    return () => clearTimeout(show);
  }, [
    respondingAt,
    isFollowing3b,
    hasPriorAssistantEntry,
    branchId,
    follow3bByDefault,
    suggestFollow3b,
  ]);

  useEffect(() => {
    if (!showFollowNudge) return;
    const hide = setTimeout(() => setShowFollowNudge(false), 5000);
    return () => clearTimeout(hide);
  }, [showFollowNudge]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.metaKey || !e.shiftKey || e.altKey || e.ctrlKey) return;
      if (e.key.toLowerCase() !== "g") return;
      e.preventDefault();
      e.stopPropagation();
      setIsFollowing3b((was) => !was);
      setShowFollowNudge(false);
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [setIsFollowing3b]);

  const stepsTouchedThisTurn = useMemo(() => {
    if (!branch?.respondingAt) return new Set<string>();
    const turnStart =
      entries.findLastIndex((e) => e.entryType === "UserMessage") + 1;
    const names = new Set<string>();
    for (let i = turnStart; i < entries.length; i++) {
      const entry = entries[i]!;
      if (entry.entryType !== "AssistantToolUse") continue;
      const step = (entry.toolCall?.input as { step?: string } | null)?.step;
      if (step) names.add(step);
    }
    return names;
  }, [branch?.respondingAt, entries]);

  const chatKey = isOnMainBranch
    ? (branchStart?.id ?? branch?.id ?? "chat")
    : (branch?.id ?? "chat");

  const prevCheckpoint = useMemo(() => {
    if (!isOnMainBranch || !commit || !branch) return undefined;
    const commitsById = new Map(branch.commits.map((c) => [c.id, c]));
    let current = commit.parentCommitId
      ? commitsById.get(commit.parentCommitId)
      : undefined;
    while (current) {
      if (current.message) {
        const checkpoints = branch.commits.filter((c) => c.message);
        const idx = checkpoints.findIndex((c) => c.id === current!.id);
        return {
          link: makeUrl(routes.space.workflow, {
            spaceId,
            workflowId,
            branchId: branch.id,
            commitId: current.id,
          }),
          versionLabel: `v${idx + 1}`,
        };
      }
      current = current.parentCommitId
        ? commitsById.get(current.parentCommitId)
        : undefined;
    }
    return undefined;
  }, [isOnMainBranch, commit, branch, spaceId, workflowId]);

  const nextCheckpoint = useMemo(() => {
    if (!isOnMainBranch || !commit || !branch || !isViewingHistory)
      return undefined;
    const checkpoints = branch.commits.filter((c) => c.message);
    const currentIdx = checkpoints.findIndex((c) => c.id === commit.id);
    // If current commit isn't a checkpoint, find the first checkpoint after it
    const startIdx =
      currentIdx >= 0
        ? currentIdx + 1
        : checkpoints.findIndex((c) => c.createdAt > commit.createdAt);
    if (startIdx < 0 || startIdx >= checkpoints.length) return undefined;
    const next = checkpoints[startIdx]!;
    const isHead = next.id === branch.headCommitId;
    return {
      link: makeUrl(routes.space.workflow, {
        spaceId,
        workflowId,
        branchId: branch.id,
        commitId: isHead ? "" : next.id,
      }),
      versionLabel: `v${startIdx + 1}`,
    };
  }, [isOnMainBranch, commit, branch, isViewingHistory, spaceId, workflowId]);

  useEffect(() => {
    if (workflowQuery.type !== "complete") return;
    if (branchQuery.type !== "complete") return;
    if (workflow?.space && !branch && urlBranchId) {
      navigate(
        routes.space.workflow,
        { spaceId, workflowId, branchId: "" },
        { replace: true }
      );
      return;
    }
    if (!workflow?.space || !branch) {
      navigate(routes.space, { spaceId }, { replace: true });
    }
  });

  const showContent = !!(workflow?.space && branch && commit);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- re-run when container mounts/unmounts
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showContent, isMobile]);

  const branchIdForTools = branch?.id ?? "";
  const mainBranchId = useMemo(
    () => workflow?.branches.find((b) => b.name === "main")?.id,
    [workflow?.branches]
  );
  const pushLiveBranch: PushLiveBranch | undefined = useMemo(
    () =>
      branch ? { id: branch.id, name: branch.name, workflowId } : undefined,
    [branch, workflowId]
  );
  const webpageDebugArgs = useMemo(
    () => ({
      drive: (stepName: string, command: DriveCommand) =>
        driveLiveStep(stepName, command, (target) => {
          forceMaximizedLiveBrowserView();
          navigate(routes.space.workflow, {
            spaceId,
            workflowId,
            step: target,
          });
        }),
    }),
    [spaceId, workflowId]
  );
  const chatTools: readonly ChatTool[] = useMemo(
    () => [
      askQuestion,
      approvePlan,
      pushLive({
        branch: pushLiveBranch,
        mainBranchId,
        spaceId,
        canPushLive,
        gitSynced: isGitSynced,
      }),
      bash({
        getStepColor: (name) => steps.find((s) => s.name === name)?.color,
      }),
      connectToApp({ branchId: branchIdForTools }),
      editFile({
        getStepColor: (name) => steps.find((s) => s.name === name)?.color,
      }),
      execute,
      getRunExecutions,
      getStepLastExecution,
      inspectRenderedPage(webpageDebugArgs),
      getPageConsole(webpageDebugArgs),
      interactWithPage(webpageDebugArgs),
      screenshotPage(webpageDebugArgs),
      readFile({
        getStepColor: (name) => steps.find((s) => s.name === name)?.color,
      }),
      listVersions,
      readVersionChat,
      runStep,
      copyUploadToStep({
        getStepColor: (name) => steps.find((s) => s.name === name)?.color,
      }),
      useSkill,
      webFetch,
      webSearch,
      writeFile({
        getStepColor: (name) => steps.find((s) => s.name === name)?.color,
      }),
    ],
    [
      steps,
      branchIdForTools,
      webpageDebugArgs,
      pushLiveBranch,
      mainBranchId,
      spaceId,
      canPushLive,
      isGitSynced,
    ]
  );

  if (!showContent) {
    return null;
  }

  const handleAskWriteReadme = () => {
    chatRef.current?.enqueueOrSend({
      text: "Write a readme for this workflow.",
      actionKind: "readme",
    });
  };

  const handleInvestigateFailure = ({
    stepDir,
    testName,
  }: {
    stepDir: string;
    testName: string;
  }) => {
    chatRef.current?.enqueueOrSend({
      text: `The “${testName}” step test for the “${stepDir}” step is failing. Run the step’s tests to reproduce it — runStepTests replays every pinned test for the step, so note which ones pass — then read the step’s code along with the failing test’s pinned input and expected output to find the root cause.

If making “${testName}” pass would break the step’s other currently-passing tests, the step is fine and this one test is the outlier — unpin it.

Explain the root cause, recommend what you think the right call is, then use askQuestion to offer (appending "(Recommended)" to whichever option you recommend):
- Fix the step — if it’s a real regression and the fix keeps the step’s other tests passing. If I pick this, make the fix and re-run the step tests to confirm they all pass.
- Unpin the step test — if the test is no longer applicable: its output isn’t deterministic, the scenario it covers is obsolete, or making it pass would break the step’s other tests. If I pick this, remove it with the unpinStepTest tool.

Don’t edit or re-pin step test files yourself, and don’t unpin a step test unless I explicitly choose to.`,
      actionKind: "investigate_step_test",
    });
  };

  const handleAskSetupStepTest = ({
    stepDir,
    executionId,
    name,
  }: StepTestFixtureSetupRequest) => {
    chatRef.current?.enqueueOrSend({
      text: `Set up and pin a step test named “${name}” for the “${stepDir}” step using execution ${executionId}. Read the execution input, the step code, Dockerfile volume declarations, and existing step-test fixtures first. If the step writes to volumes, provide only the initial volume fixtures needed to exercise the behavior; author SQLite databases as sqlite-dump SQL text (CREATE TABLE and INSERT statements), and use base64 only for other binary files. If the step writes a store that isn’t SQLite-compatible, first add a tests/serialize script to the step (POSIX sh, invoked as sh tests/serialize <volume-name>) that prints a stable text representation of that volume’s entire state, so the test compares meaning rather than raw bytes. Then pin the step test and run it to confirm it passes.`,
      actionKind: "step_test_fixture_setup",
      selectedStep: stepDir,
    });
  };

  const handleChatSend: ChatProps["onSend"] = (payload, onError) => {
    void navigateOnBranchChange((branchParams) => {
      if (isOnMainBranch) {
        markBranchAwaitingAiName(branchParams.forkBranchId);
      }
      const result = z.mutate(
        sharedMutators.sendMessage({
          ...branchParams,
          entryId: nanoid(12),
          text: payload.text,
          isDictated: payload.isDictated,
          attachments: payload.attachments,
          selectedStep: payload.selectedStep,
          interruptEntryId: payload.interruptEntryId,
          planMode: payload.planMode,
          effort: payload.effort,
          chatModel: payload.chatModel,
          actionKind: payload.actionKind,
          clientSentAt: Date.now(),
          clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      );
      if (isOnMainBranch) gatedMutate(result);
      void result.server.then((r) => {
        if (r.type === "error") onError();
      });
      return result;
    });
  };

  const handleChatInterrupt = () => {
    if (!headCommitChatId) return;
    z.mutate(
      sharedMutators.interruptChat({
        entryId: nanoid(12),
        chatId: headCommitChatId,
        branchId: branch.id,
      })
    );
  };

  const handleChatProvideToolResult: ChatProps["onProvideToolResult"] = (
    entryId,
    output,
    isError,
    options
  ) => {
    if (!headCommitChatId) return;
    z.mutate(
      sharedMutators.provideSpaceChatToolResult({
        chatId: headCommitChatId,
        entryId,
        output,
        isError,
        branchId: branch.id,
        pauseAfter: options?.pauseAfter,
        chatModel: options?.chatModel,
      })
    );
  };

  const layout = layoutWorkflowSteps(steps, links, {
    compareIsolated: (a, b) => rankIsolatedStep(a) - rankIsolatedStep(b),
  });
  const cells = Array.from(layout.values());
  const rowCount = Math.max(...cells.map((p) => p.row), -1) + 1;
  const { padTop, padBottom } = getStepMapPadding(layout, links);

  const hasSteps = steps.length > 0;
  const fullStepMapHeight = hasSteps
    ? padTop +
      padBottom +
      rowCount * gridStepHeight +
      Math.max(rowCount - 1, 0) * rowGap
    : ghostStepMapHeight;
  const defaultStepMapHeight =
    containerHeight > 0
      ? Math.min(
          fullStepMapHeight,
          containerHeight * 0.5,
          Math.max(0, containerHeight - 400 - gridPadding * 2)
        )
      : null;

  return (
    <>
      <title>{`${workflow.name} • ${config.title}`}</title>
      <Backdrop
        overrideLogo={(() => {
          const badgeConfig = parseBadgeConfig(workflow.badgeSeed, workflow.id);
          return (
            <div
              style={{
                width: 16,
                height: 16,
                flexShrink: 0,
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <CircleMesh
                key={workflow.badgeSeed}
                targetCircleSize={8}
                seed={badgeConfig.seed}
                mutingIntensity={0}
                animate={false}
                restrictedColors={badgeConfig.colors}
                grayscale={workflow.pausedAt != null}
              />
            </div>
          );
        })()}
        afterPath={
          isMobile ? (
            <Text
              variant="text-regular-md"
              ellipsis
              style={{ minWidth: 0, flex: 1 }}
            >
              <EditableWorkflowName workflow={workflow} readOnly={!canWrite} />
            </Text>
          ) : (
            <>
              <Text variant="text-regular-md">
                <EditableWorkflowName
                  workflow={workflow}
                  readOnly={!canWrite}
                />
              </Text>
              <div style={{ width: 8 }} />
              <SpaceBadge space={workflow.space} asLink />
            </>
          )
        }
        rightPrefix={
          isMobile ? (
            <WorkflowActionsMenu
              workflowId={workflowId}
              spaceId={spaceId}
              isFavorite={isFavorite}
              autofixEnabled={workflow.autofixEnabled ?? true}
              autoImproveEnabled={workflow.autoImproveEnabled ?? false}
              opener={
                <Button
                  variant="secondary"
                  size="l"
                  icon="more"
                  aria-label="More options"
                />
              }
              afterDelete={() =>
                navigate(routes.space, { spaceId }, { replace: true })
              }
            />
          ) : (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <WorkflowConnectorButton
                workflowId={workflowId}
                spaceId={spaceId}
                workflowName={workflow.name}
                workflowBadgeSeed={workflow.badgeSeed}
              />
              <WorkflowActionsMenu
                workflowId={workflowId}
                spaceId={spaceId}
                isFavorite={isFavorite}
                autofixEnabled={workflow.autofixEnabled ?? true}
                autoImproveEnabled={workflow.autoImproveEnabled ?? false}
                opener={
                  <Button
                    variant="secondary"
                    size="l"
                    icon="more"
                    aria-label="More options"
                  />
                }
                afterDelete={() =>
                  navigate(routes.space, { spaceId }, { replace: true })
                }
              />
            </div>
          )
        }
      >
        <MainScrollContainer
          style={isMobile ? { overflow: "hidden" } : undefined}
        >
          <Layout
            ref={layoutRef}
            style={
              isMobile
                ? {
                    flexDirection: "column",
                    gap: 0,
                    padding: "0 8px 8px",
                  }
                : undefined
            }
          >
            <ChatContainer
              style={
                isMobile
                  ? {
                      width: "100%",
                      flex: 1,
                      minHeight: 0,
                    }
                  : {
                      ...(chatWidth != null && { width: chatWidth }),
                      ...(!chatDraggingRef.current && {
                        transition: "width 160ms ease-out",
                      }),
                    }
              }
            >
              <BranchSelector
                workflow={workflow}
                currentBranch={branch}
                branchSummaries={workflow.branches}
                commitId={commit.id}
                spaceId={spaceId}
                presentBranchesByUser={presentBranchesByUser}
                chatEntries={downloadChatEntries}
                hasChangesToPush={hasChangesToPush}
                canWrite={canWrite}
                canPushLive={canPushLive}
                isDeletedOnBranch={isDeletedOnBranch}
                isDeploying={isDeploying}
                isMerging={isMerging}
                onDeploying={setIsDeploying}
                onMerging={setIsMerging}
              />
              <Chat
                ref={chatRef}
                key={chatKey}
                modelScope={{ type: "branch", branchId: branch.id }}
                onZipFile={setPendingZip}
                entries={entries}
                writeChatId={headCommitChatId}
                draftKey={urlBranchId || "main"}
                onSend={handleChatSend}
                onInterrupt={handleChatInterrupt}
                onProvideToolResult={handleChatProvideToolResult}
                prevCheckpointLink={prevCheckpoint?.link}
                prevCheckpointLabel={prevCheckpoint?.versionLabel}
                nextCheckpointLink={nextCheckpoint?.link}
                nextCheckpointLabel={nextCheckpoint?.versionLabel}
                isCurrentUserChat={!isViewingHistory && !isDeletedOnBranch}
                canWrite={canWrite}
                tools={chatTools}
                spaceId={spaceId}
                suggestions={
                  entries.length > 0 || hasSteps
                    ? workflowSuggestions
                    : gettingStartedSuggestions
                }
                soundsEnabled={soundsEnabled}
                respondingAt={branch.respondingAt}
                awaitingAction={branch.awaitingAction ?? false}
                planModeDefaultSurface={isOnMainBranch ? "branch" : "none"}
                codeSnippets={codeSnippets}
                removeCodeSnippet={removeCodeSnippet}
                clearCodeSnippets={clearCodeSnippets}
                elementSnippets={elementSnippets}
                removeElementSnippet={removeElementSnippet}
                clearElementSnippets={clearElementSnippets}
                selectedStepName={selectedStep?.name}
                resolveFileHref={resolveChatFileHref}
                historyBanner={
                  isViewingHistory ? (
                    <HistoryBanner branch={branch} commit={commit} />
                  ) : isDeletedOnBranch ? (
                    <DeletedOnBranchBanner branch={branch} />
                  ) : undefined
                }
                pendingPromptOverride={
                  connectingClaimId ? (
                    <ConnectClaimPrompt
                      claimId={connectingClaimId}
                      onClose={() =>
                        navigate(
                          null,
                          { connectingClaimId: "" },
                          { replace: true }
                        )
                      }
                    />
                  ) : undefined
                }
                onStatusClick={() => setIsFollowing3b((was) => !was)}
                followActive={isFollowing3b}
                showFollowNudge={showFollowNudge}
                onDismissFollowNudge={() => setShowFollowNudge(false)}
                onStreamingToolStep={setStreamingAgentStep}
                onStatusChange={setFollowStatus}
                onStreamingFileWrite={setStreamingFileWrite}
                onStreamingFileEdit={setStreamingFileEdit}
                onStreamingFileTarget={setStreamingFileTarget}
              />
              {pendingZip && (
                <ImportWorkflowModal
                  file={pendingZip}
                  defaultSpaceId={spaceId}
                  onClose={() => setPendingZip(null)}
                />
              )}
            </ChatContainer>

            {!isMobile && (
              <Vertical ref={containerRef}>
                <ChatResizeHandle
                  onMouseDown={onChatResizeHandleDown}
                  onDoubleClick={handleChatResizeDoubleClick}
                >
                  <div
                    data-drag-indicator
                    style={{
                      height: 32,
                      width: 2,
                      background: "var(--border-alpha)",
                      borderRadius: 10,
                    }}
                  />
                </ChatResizeHandle>
                {containerHeight > 0 && (
                  <Resizable
                    Handle={
                      <div
                        style={{
                          height: gridPadding * 2,
                          marginBottom: -(gridPadding * 2),
                          display: "flex",
                          paddingTop: 4,
                          justifyContent: "center",
                        }}
                      >
                        <div
                          data-drag-indicator
                          style={{
                            width: 32,
                            height: 2,
                            background: "var(--border-alpha)",
                            borderRadius: 10,
                          }}
                        />
                      </div>
                    }
                    minSize={Math.max(gridStepHeight + gridPadding * 2, 60)}
                    maxSize={fullStepMapHeight}
                    minOppositeSize={400}
                    defaultSize={defaultStepMapHeight ?? fullStepMapHeight}
                    orientation="vertical"
                    containerRef={containerRef}
                    first={(() => {
                      const topContent =
                        commitFilesLoading ? null : selectedStep ? (
                          <MaximizedStep
                            allConnectors={allConnectors}
                            key={selectedStep.name}
                            step={selectedStep}
                            allSteps={steps}
                            spaceSlug={workflow.space.slug}
                            spaceId={spaceId}
                            branch={branch}
                            commitId={commit.id}
                            files={selectStepFiles(
                              activeCommitFiles,
                              selectedStep.name
                            )}
                            baseFiles={
                              branch.forkCommitId &&
                              branch.forkCommitId !== commit.id
                                ? selectStepFiles(
                                    forkCommitFiles,
                                    selectedStep.name
                                  )
                                : undefined
                            }
                            isReadOnly={isViewingHistory || isDeletedOnBranch}
                            canWrite={canWrite}
                            onInvestigateFailure={handleInvestigateFailure}
                            onAskSetupStepTest={handleAskSetupStepTest}
                            workflowPausedAt={workflow.pausedAt ?? null}
                            onSelectionChange={handleCodeSelectionChange}
                            codeSnippets={codeSnippets}
                            onElementSelect={handleElementSelection}
                            onElementDeselect={deselectElementByIndex}
                            htmlInspectMode={htmlInspectMode}
                            onHtmlInspectModeChange={setHtmlInspectMode}
                            elementSnippets={elementSnippets.filter(
                              (s) => s.stepName === selectedStep.name
                            )}
                            isBeingWritten={stepsTouchedThisTurn.has(
                              selectedStep.name
                            )}
                            forceShowCode={isFollowing3b}
                            onStopFollow={() => setIsFollowing3b(false)}
                            expandOutput={isFollowing3b && expandOutput}
                            followActive={
                              isFollowing3b && followStatus === "building"
                            }
                            streamingFileWrite={
                              streamingFileWrite?.stepDir === selectedStep.name
                                ? streamingFileWrite
                                : null
                            }
                            streamingFileEdit={
                              streamingFileEdit?.stepDir === selectedStep.name
                                ? streamingFileEdit
                                : null
                            }
                          />
                        ) : (
                          <WorkflowOverview
                            key={branch.id}
                            workflowId={workflowId}
                            branchId={branch.id}
                            spaceSlug={workflow.space.slug}
                            spaceId={spaceId}
                            branch={branch}
                            canWrite={canWrite}
                            readme={workflowReadme}
                            steps={steps}
                            allConnectors={allConnectors}
                            inspectMode={htmlInspectMode}
                            onInspectModeChange={setHtmlInspectMode}
                            onElementSelect={handleElementSelection}
                            onElementDeselect={deselectElementByIndex}
                            elementSnippets={elementSnippets}
                            hoveredCanonicalId={hoveredStepCanonicalId}
                            onStepHover={setHoveredStepCanonicalId}
                            onStepsHover={handleMonitorStepsHover}
                            onRunsTabStepsChange={handleRunsTabStepsChange}
                            onAskWriteReadme={handleAskWriteReadme}
                            onInvestigateFailure={handleInvestigateFailure}
                            onAskSetupStepTest={handleAskSetupStepTest}
                          />
                        );

                      return (
                        <div
                          ref={topPaneRef}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                            overflow: "hidden",
                          }}
                        >
                          {topContent}
                          {showRuns && selectedStep && (
                            <>
                              <DragHandle
                                onMouseDown={onWaterfallHandleDown}
                                onDoubleClick={() => setWaterfallHeight(null)}
                              >
                                <div
                                  data-drag-indicator
                                  style={{
                                    width: 32,
                                    height: 2,
                                    background: "var(--border-alpha)",
                                    borderRadius: 10,
                                  }}
                                />
                              </DragHandle>
                              <div
                                style={{
                                  height:
                                    waterfallHeight ??
                                    waterfallIdealHeight ??
                                    "50%",
                                  flexShrink: 0,
                                  minHeight: 240,
                                  maxHeight: "50%",
                                  overflow: "hidden",
                                  padding: "0 12px",
                                }}
                              >
                                <RunWaterfall
                                  runId={selectedRunId}
                                  steps={steps}
                                  selectedStepDir={step ?? null}
                                  hoveredCanonicalId={hoveredStepCanonicalId}
                                  onStepHover={setHoveredStepCanonicalId}
                                  onIdealHeight={setWaterfallIdealHeight}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                    second={
                      hasSteps ? (
                        <StepMap
                          key={branch.id}
                          steps={steps}
                          links={links}
                          workflow={workflow}
                          spaceSlug={workflow.space.slug}
                          layout={layout}
                          selectedStep={step ?? null}
                          branchId={branch.id}
                          commitId={commit.id}
                          isOnMainBranch={isOnMainBranch}
                          isReadOnly={isViewingHistory || isDeletedOnBranch}
                          canWrite={canWrite}
                          aiPresentOnStepName={aiPresentOnStepName}
                          presentByStep={presentByStep}
                          enableSounds={soundsEnabled}
                          changedCanonicalIds={changedCanonicalIds}
                          highlightedCanonicalIds={
                            monitorHoveredStepIds ??
                            (showRuns
                              ? activeRunCanonicalIds
                              : !selectedStep && tab === "runs"
                                ? runsTabStepIds
                                : null)
                          }
                          hoveredCanonicalId={hoveredStepCanonicalId}
                          onStepHover={setHoveredStepCanonicalId}
                        />
                      ) : (
                        <GhostedStepGrid />
                      )
                    }
                  />
                )}
              </Vertical>
            )}
          </Layout>
        </MainScrollContainer>
      </Backdrop>
    </>
  );
}
